import * as type from '../actions/types';
import cloneDeep from 'lodash/cloneDeep';

var initialState = {
  sessionUuid: null,
  messages: [],
  pendingAttachments: [],
  serverDocumentCount: 0,
  streamStatus: 'idle', // 'idle' | 'streaming' | 'error'
  streamError: null,
  toolStatuses: [],
  takingAWhile: false,
  thinkingLabel: null,
  recovering: false,
};

export default function chatSession(state, action) {
  if (state === undefined) state = initialState;

  switch (action.type) {
    case type.CHAT_SESSION_HYDRATE: {
      return Object.assign({}, initialState, action.payload);
    }
    case type.CHAT_SESSION_SET_UUID: {
      return Object.assign({}, state, { sessionUuid: action.payload.sessionUuid });
    }
    case type.CHAT_SESSION_RECONCILED: {
      return Object.assign({}, state, {
        messages: action.payload.messages,
        serverDocumentCount: action.payload.serverDocumentCount,
      });
    }
    case type.CHAT_SESSION_CLEARED: {
      return Object.assign({}, initialState);
    }
    case type.CHAT_MESSAGE_QUEUED: {
      var next = cloneDeep(state);
      next.messages.push(action.payload.message);
      return next;
    }
    case type.CHAT_ASSISTANT_PLACEHOLDER_PUSHED: {
      var nextPh = cloneDeep(state);
      nextPh.messages.push({
        uuid: action.payload.uuid,
        role: 'assistant',
        text: '',
        status: 'streaming',
        createdAt: action.payload.createdAt,
        attachments: [],
      });
      nextPh.thinkingLabel = null;
      return nextPh;
    }
    case type.CHAT_USER_MESSAGE_UUID_ASSIGNED: {
      var nextUu = cloneDeep(state);
      // The most recent user message without a uuid receives this one.
      for (var ui = nextUu.messages.length - 1; ui >= 0; ui--) {
        var um = nextUu.messages[ui];
        if (um.role === 'user' && !um.uuid) {
          um.uuid = action.payload.uuid;
          break;
        }
      }
      return nextUu;
    }
    case type.CHAT_MESSAGE_APPEND_DELTA: {
      // Append accumulated paragraph to the currently-streaming assistant message.
      // Multiple assistant messages can exist in one HTTP turn (one per tool-loop
      // iteration), so target by uuid when supplied, else the last streaming bubble.
      var next2 = cloneDeep(state);
      var targetUuid = action.payload.uuid;
      var target = null;
      for (var di = next2.messages.length - 1; di >= 0; di--) {
        var dm = next2.messages[di];
        if (dm.role !== 'assistant') continue;
        if (targetUuid && dm.uuid === targetUuid) { target = dm; break; }
        if (!targetUuid && dm.status === 'streaming') { target = dm; break; }
      }
      if (target) {
        var existing = target.text || '';
        target.text = existing.length > 0
          ? existing + '\n\n' + action.payload.text
          : action.payload.text;
      }
      return next2;
    }
    case type.CHAT_MESSAGE_END: {
      var next3 = cloneDeep(state);
      var endUuid = action.payload && action.payload.uuid;
      for (var ei = next3.messages.length - 1; ei >= 0; ei--) {
        var em = next3.messages[ei];
        if (em.role !== 'assistant') continue;
        if (endUuid ? em.uuid === endUuid : em.status === 'streaming') {
          em.status = 'complete';
          break;
        }
      }
      next3.thinkingLabel = null;
      return next3;
    }
    case type.CHAT_MESSAGE_INTERRUPTED:
    case type.CHAT_MESSAGE_ERRORED: {
      var next4 = cloneDeep(state);
      var lastMsg = next4.messages[next4.messages.length - 1];
      if (lastMsg && lastMsg.role === 'assistant') {
        lastMsg.status = action.type === type.CHAT_MESSAGE_INTERRUPTED ? 'interrupted' : 'errored';
        lastMsg.errorCode = action.payload && action.payload.code;
        lastMsg.errorMessage = action.payload && action.payload.message;
      } else {
        // Stream errored before any assistant text — append a stub assistant message
        next4.messages.push({
          id: action.payload && action.payload.id,
          role: 'assistant',
          text: '',
          status: action.type === type.CHAT_MESSAGE_INTERRUPTED ? 'interrupted' : 'errored',
          errorCode: action.payload && action.payload.code,
          errorMessage: action.payload && action.payload.message,
          createdAt: new Date().toISOString(),
          attachments: [],
        });
      }
      next4.thinkingLabel = null;
      return next4;
    }
    case type.CHAT_TOOL_START: {
      var nextTs = cloneDeep(state);
      nextTs.toolStatuses.push({
        toolUseId: action.payload.toolUseId,
        name: action.payload.name,
        label: action.payload.label,
        message: action.payload.label,
      });
      nextTs.takingAWhile = false;
      nextTs.thinkingLabel = null;
      return nextTs;
    }
    case type.CHAT_TOOL_PROGRESS: {
      var idxP = -1;
      for (var pi = 0; pi < state.toolStatuses.length; pi++) {
        if (state.toolStatuses[pi].toolUseId === action.payload.toolUseId) { idxP = pi; break; }
      }
      if (idxP === -1) return state;
      var nextTp = cloneDeep(state);
      nextTp.toolStatuses[idxP].message = action.payload.message;
      nextTp.takingAWhile = false;
      return nextTp;
    }
    case type.CHAT_TOOL_END: {
      if (!action.payload || !action.payload.toolUseId) return state;
      var nextTe = cloneDeep(state);
      nextTe.toolStatuses = nextTe.toolStatuses.filter(function (ts) {
        return ts.toolUseId !== action.payload.toolUseId;
      });
      nextTe.takingAWhile = false;
      return nextTe;
    }
    case type.CHAT_STREAM_STARTED: {
      return Object.assign({}, state, {
        streamStatus: 'streaming',
        streamError: null,
        toolStatuses: [],
        takingAWhile: false,
        thinkingLabel: null,
      });
    }
    case type.CHAT_STREAM_ENDED: {
      return Object.assign({}, state, {
        streamStatus: 'idle',
        toolStatuses: [],
        takingAWhile: false,
        thinkingLabel: null,
        recovering: false,
        streamError: action.payload && action.payload.error ? action.payload.error : null,
      });
    }
    case type.CHAT_STREAM_TAKING_A_WHILE: {
      return Object.assign({}, state, { takingAWhile: true });
    }
    case type.CHAT_THINKING_LABEL_SET: {
      return Object.assign({}, state, { thinkingLabel: action.payload.label, takingAWhile: false });
    }
    case type.CHAT_THINKING_LABEL_CLEARED: {
      return Object.assign({}, state, { thinkingLabel: null });
    }
    case type.CHAT_RECOVERY_STARTED: {
      return Object.assign({}, state, { recovering: true, thinkingLabel: null });
    }
    case type.CHAT_RECOVERY_ENDED: {
      return Object.assign({}, state, { recovering: false });
    }
    case type.CHAT_SERVER_DOCUMENT_COUNT_INCREMENTED: {
      return Object.assign({}, state, {
        serverDocumentCount: state.serverDocumentCount + action.payload.delta,
      });
    }
    case type.CHAT_PENDING_ATTACHMENT_ADDED: {
      var next5 = cloneDeep(state);
      next5.pendingAttachments.push(action.payload.attachment);
      return next5;
    }
    case type.CHAT_PENDING_ATTACHMENT_REMOVED: {
      var next6 = cloneDeep(state);
      next6.pendingAttachments.splice(action.payload.index, 1);
      return next6;
    }
    case type.CHAT_PENDING_ATTACHMENTS_CLEARED: {
      return Object.assign({}, state, { pendingAttachments: [] });
    }
    case type.CLEAR_SLATE_VIEWS: {
      return Object.assign({}, initialState);
    }
    default:
      return state;
  }
}
